/* src/modules/feedback/FounderChannel.js */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
    collection, addDoc, query, where, orderBy, onSnapshot, 
    doc, updateDoc, serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { 
    ChatAltIcon, PlusIcon, PaperAirplaneIcon, EyeIcon, EyeOffIcon,
    SearchIcon, ChevronLeftIcon, ChevronRightIcon,
    UserIcon, LockClosedIcon, CheckCircleIcon
} from '../../components/icons/Icons';

// --- HELPERS ---
const formatDate = (timestamp) => {
    if (!timestamp) return 'Just now';
    return new Date(timestamp.seconds * 1000).toLocaleDateString() + ' ' + 
           new Date(timestamp.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Reusable Avatar (Updated to support Photo URL)
const UserAvatar = ({ name, photoURL, isFounder, className = "h-8 w-8" }) => {
    const [imgError, setImgError] = useState(false);
    
    // If it's a founder or user with a valid photo, try showing it
    if (photoURL && !imgError) {
        return (
            <img 
                src={photoURL} 
                alt={name} 
                className={`${className} rounded-full object-cover border border-gray-200 shrink-0`} 
                onError={() => setImgError(true)} 
            />
        );
    }

    // Fallback to Initials
    return (
        <div className={`${className} rounded-full flex items-center justify-center font-bold text-xs shrink-0 border ${
            isFounder ? 'bg-orange-100 text-orange-800 border-orange-200' : 'bg-blue-100 text-[#213368] border-blue-200'
        }`}>
            {name ? name.charAt(0).toUpperCase() : <UserIcon className="h-4 w-4" />}
        </div>
    );
};

export default function FounderChannel({ userProfile, appId, isFounder }) {
    const [threads, setThreads] = useState([]);
    
    // ✅ FIX: Store ID only, so we always pull the latest data from the live 'threads' array
    const [selectedThreadId, setSelectedThreadId] = useState(null);
    
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    
    // Filters & Pagination
    const [searchQuery, setSearchQuery] = useState('');
    const [timeFilter, setTimeFilter] = useState('All'); 
    const [statusFilter, setStatusFilter] = useState('All'); 
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6; 

    // 1. Fetch Threads
    useEffect(() => {
        const ref = collection(db, `artifacts/${appId}/founder_feedback`);
        let q;

        if (isFounder) {
            q = query(ref, orderBy('lastReplyAt', 'desc'));
        } else {
            q = query(ref, where('authorId', '==', userProfile.id), orderBy('createdAt', 'desc'));
        }

        const unsub = onSnapshot(q, (snap) => {
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setThreads(data);
        });
        return () => unsub();
    }, [appId, isFounder, userProfile.id]);

    // ✅ Helper to get the live selected thread object
    const selectedThread = useMemo(() => 
        threads.find(t => t.id === selectedThreadId) || null
    , [threads, selectedThreadId]);

    // Reset page on filter change
    useEffect(() => { setCurrentPage(1); }, [searchQuery, timeFilter, statusFilter]);

    // 2. Create Logic
    const handleCreate = async (subject, content, isAnon) => {
        // A. Create Parent Thread
        const threadRef = await addDoc(collection(db, `artifacts/${appId}/founder_feedback`), {
            subject: subject, 
            content: content, 
            authorId: userProfile.id,
            authorName: isAnon ? 'Anonymous User' : userProfile.name,
            authorEmail: userProfile.email,
            // ✅ Save Dept/Photo for non-anon display
            authorDept: isAnon ? null : (userProfile.department || 'General'),
            authorPhoto: isAnon ? null : (userProfile.photoURL || null),
            isAnonymous: isAnon,
            status: 'Open',
            createdAt: serverTimestamp(),
            lastReplyAt: serverTimestamp(),
            unreadFounder: true,
            unreadUser: false
        });

        // B. Add First Message
        await addDoc(collection(db, `artifacts/${appId}/founder_feedback/${threadRef.id}/messages`), {
            content: content,
            senderId: userProfile.id,
            senderName: isAnon ? 'Anonymous User' : userProfile.name,
            isFounderReply: false,
            createdAt: serverTimestamp()
        });

        setIsCreateOpen(false);
        // Optional: Auto-select the new thread
        setSelectedThreadId(threadRef.id);
    };

    // 3. Filter Logic
    const filteredThreads = useMemo(() => {
        return threads.filter(t => {
            const q = searchQuery.toLowerCase();
            const matchSearch = !q || 
                (t.subject && t.subject.toLowerCase().includes(q)) || 
                (t.content && t.content.toLowerCase().includes(q)) ||
                (t.authorName && t.authorName.toLowerCase().includes(q));

            const matchStatus = statusFilter === 'All' || t.status === statusFilter;

            let matchTime = true;
            if (timeFilter !== 'All' && t.createdAt) {
                const date = t.createdAt.toDate();
                const now = new Date();
                const diffTime = Math.abs(now - date);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                if (timeFilter === 'Today') matchTime = diffDays <= 1;
                else if (timeFilter === 'Week') matchTime = diffDays <= 7;
                else if (timeFilter === 'Month') matchTime = diffDays <= 30;
                else if (timeFilter === 'Year') matchTime = diffDays <= 365;
            }

            return matchSearch && matchStatus && matchTime;
        });
    }, [threads, searchQuery, statusFilter, timeFilter]);

    // 4. Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredThreads.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredThreads.length / itemsPerPage);

    return (
        <div className="flex flex-col md:flex-row h-[700px] border-t border-gray-100">
            {/* --- LEFT SIDEBAR (List) --- */}
            <div className={`w-full md:w-[35%] border-r border-gray-200 flex flex-col bg-gray-50 ${selectedThreadId ? 'hidden md:flex' : 'flex'}`}>
                
                <div className="p-4 border-b border-gray-200 bg-white space-y-3">
                    <div className="flex justify-between items-center">
                        <h3 className="font-bold text-[#213368]">{isFounder ? 'Inbox' : 'My Conversations'}</h3>
                        {!isFounder && (
                            <button onClick={() => setIsCreateOpen(true)} className="p-1.5 bg-[#20BEC6] text-white rounded-md shadow-sm hover:opacity-90 transition">
                                <PlusIcon className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                    {/* Search/Filter Controls... */}
                    <div className="relative">
                        <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                        <input type="text" placeholder="Search subjects..." className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-1 focus:ring-[#20BEC6] outline-none" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                    </div>
                    <div className="flex gap-2">
                        <select className="flex-1 bg-white border border-gray-300 text-gray-600 py-1.5 px-2 rounded-lg text-xs outline-none" value={timeFilter} onChange={e => setTimeFilter(e.target.value)}>
                            <option value="All">Any Time</option>
                            <option value="Today">Today</option>
                            <option value="Week">This Week</option>
                            <option value="Month">This Month</option>
                            <option value="Year">This Year</option>
                        </select>
                        <select className="flex-1 bg-white border border-gray-300 text-gray-600 py-1.5 px-2 rounded-lg text-xs outline-none" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                            <option value="All">All Status</option>
                            <option value="Open">Open</option>
                            <option value="Closed">Closed</option>
                        </select>
                    </div>
                </div>
                
                <div className="flex-1 overflow-y-auto">
                    {filteredThreads.length === 0 && <p className="text-center text-gray-400 mt-10 text-xs italic">No conversations found.</p>}
                    
                    {currentItems.map(t => (
                        <div 
                            key={t.id} 
                            onClick={() => setSelectedThreadId(t.id)} // ✅ Update ID only
                            className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-blue-50 transition group ${selectedThreadId === t.id ? 'bg-white border-l-4 border-l-[#213368] shadow-sm' : 'bg-transparent'}`}
                        >
                            <div className="flex justify-between items-start mb-1">
                                <div className="flex items-center gap-2">
                                    {/* ✅ Avatar in List */}
                                    <UserAvatar 
                                        name={t.authorName} 
                                        photoURL={t.authorPhoto} 
                                        isFounder={false} 
                                        className="h-6 w-6" 
                                    />
                                    <div className="flex flex-col">
                                        <span className={`font-bold text-sm line-clamp-1 ${selectedThreadId === t.id ? 'text-[#213368]' : 'text-gray-700'}`}>
                                            {t.subject || '(No Subject)'}
                                        </span>
                                        {/* ✅ Show Dept if available */}
                                        {!t.isAnonymous && t.authorDept && (
                                            <span className="text-[9px] text-gray-400 font-medium">{t.authorDept}</span>
                                        )}
                                    </div>
                                </div>
                                <span className="text-[10px] text-gray-400 whitespace-nowrap ml-2">{formatDate(t.lastReplyAt)}</span>
                            </div>
                            
                            <div className="flex justify-between items-center mt-2 pl-8">
                                <span className="text-xs text-gray-500 truncate w-3/4">
                                    {t.content}
                                </span>
                                {t.status === 'Closed' && <LockClosedIcon className="h-3 w-3 text-gray-300" />}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Pagination Controls */}
                {filteredThreads.length > itemsPerPage && (
                    <div className="p-3 border-t border-gray-200 bg-white flex justify-between items-center">
                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronLeftIcon className="h-4 w-4 text-gray-600"/></button>
                        <span className="text-xs font-medium text-gray-500">Page {currentPage} of {totalPages}</span>
                        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronRightIcon className="h-4 w-4 text-gray-600"/></button>
                    </div>
                )}
            </div>

            {/* --- RIGHT SIDE (Thread View) --- */}
            <div className={`w-full md:w-[65%] flex flex-col bg-white ${!selectedThreadId ? 'hidden md:flex' : 'flex'}`}>
                {selectedThread ? (
                    <ThreadDetail 
                        appId={appId} 
                        thread={selectedThread} // ✅ Passed live object
                        currentUser={userProfile} 
                        isFounder={isFounder} 
                        onBack={() => setSelectedThreadId(null)} 
                    />
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-300 p-8 text-center">
                        <div className="bg-gray-50 p-6 rounded-full mb-4">
                            <ChatAltIcon className="h-12 w-12 text-gray-300" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-400">Select a conversation</h3>
                        <p className="text-sm max-w-xs mt-2">Choose a thread from the list to view the discussion history.</p>
                    </div>
                )}
            </div>

            {/* Create Modal */}
            {isCreateOpen && <CreateFeedbackModal onClose={() => setIsCreateOpen(false)} onSubmit={handleCreate} />}
        </div>
    );
}

// --- SUB-COMPONENT: THREAD VIEW ---
function ThreadDetail({ appId, thread, currentUser, isFounder, onBack }) {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const scrollRef = useRef(null);

    useEffect(() => {
        if(!thread) return;
        const q = query(collection(db, `artifacts/${appId}/founder_feedback/${thread.id}/messages`), orderBy('createdAt', 'asc'));
        const unsub = onSnapshot(q, (snap) => {
            setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
        });
        return () => unsub();
    }, [appId, thread]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;

        await addDoc(collection(db, `artifacts/${appId}/founder_feedback/${thread.id}/messages`), {
            content: newMessage,
            senderId: currentUser.id,
            senderName: isFounder ? currentUser.name : (thread.isAnonymous ? 'Anonymous' : currentUser.name),
            isFounderReply: isFounder,
            createdAt: serverTimestamp()
        });

        await updateDoc(doc(db, `artifacts/${appId}/founder_feedback`, thread.id), {
            lastReplyAt: serverTimestamp(),
            content: newMessage, 
            status: 'Open',
            unreadUser: isFounder,
            unreadFounder: !isFounder 
        });
        setNewMessage('');
    };

    const handleCloseThread = async () => {
        if (window.confirm("Mark this conversation as resolved?")) {
            await updateDoc(doc(db, `artifacts/${appId}/founder_feedback`, thread.id), { status: 'Closed' });
            // ✅ No need to manually update state; the parent snapshot will trigger re-render
        }
    };

    if (!thread) return null;

    return (
        <div className="flex flex-col h-full relative">
            {/* Thread Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-start bg-white z-10">
                <div className="flex items-start overflow-hidden">
                    <button onClick={onBack} className="md:hidden mr-3 mt-1 text-gray-500"><ChevronLeftIcon className="h-5 w-5"/></button>
                    <div>
                        <h2 className="font-bold text-[#213368] text-lg leading-tight line-clamp-1">{thread.subject}</h2>
                        <div className="flex items-center mt-2 text-xs text-gray-500 gap-3">
                            <span className={`px-2 py-0.5 rounded-full font-bold flex items-center ${thread.status === 'Closed' ? 'bg-gray-100 text-gray-500' : 'bg-green-50 text-green-700 border border-green-100'}`}>
                                {thread.status === 'Closed' ? <LockClosedIcon className="h-3 w-3 mr-1"/> : <CheckCircleIcon className="h-3 w-3 mr-1"/>}
                                {thread.status}
                            </span>
                            
                            {/* ✅ Author Details in Header */}
                            <div className="flex items-center gap-2">
                                <UserAvatar name={thread.authorName} photoURL={thread.authorPhoto} className="h-5 w-5" />
                                <span>{isFounder ? thread.authorName : 'You'}</span>
                                {!thread.isAnonymous && thread.authorDept && (
                                    <span className="bg-gray-100 px-1.5 py-0.5 rounded">{thread.authorDept}</span>
                                )}
                            </div>
                            
                            <span>•</span>
                            <span>{formatDate(thread.createdAt)}</span>
                        </div>
                    </div>
                </div>
                {isFounder && thread.status === 'Open' && (
                    <button onClick={handleCloseThread} className="text-xs bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-600 px-3 py-1.5 rounded font-medium transition">
                        Mark Resolved
                    </button>
                )}
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50/50">
                {messages.length === 0 && <div className="text-center text-xs text-gray-400 my-4">Conversation started.</div>}
                
                {messages.map(msg => {
                    const isMe = msg.senderId === currentUser.id;
                    const isFounderMsg = msg.isFounderReply;

                    return (
                        <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                            {/* Metadata */}
                            <div className="flex items-center mb-1 space-x-2 px-1">
                                <span className={`text-xs font-bold ${isFounderMsg ? 'text-orange-700' : 'text-[#213368]'}`}>
                                    {isMe ? 'You' : msg.senderName}
                                </span>
                                {isFounderMsg && (
                                    <span className="text-[9px] bg-orange-100 text-orange-700 px-1.5 rounded border border-orange-200 uppercase font-bold tracking-wider">
                                        Founder
                                    </span>
                                )}
                                <span className="text-[10px] text-gray-400">{formatDate(msg.createdAt)}</span>
                            </div>

                            {/* Message Box */}
                            <div className={`p-4 rounded-xl shadow-sm max-w-[90%] text-sm leading-relaxed whitespace-pre-wrap break-words ${
                                isMe 
                                ? 'bg-white border border-gray-200 text-gray-800 rounded-tr-none' 
                                : isFounderMsg 
                                    ? 'bg-orange-50/50 border border-orange-100 text-gray-800 rounded-tl-none'
                                    : 'bg-white border border-gray-200 text-gray-800 rounded-tl-none'
                            }`}>
                                {msg.content}
                            </div>
                        </div>
                    );
                })}
                <div ref={scrollRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 bg-white border-t border-gray-200">
                <form onSubmit={handleSend} className="relative">
                    <textarea 
                        className="w-full bg-gray-50 border border-gray-200 rounded-lg pl-4 pr-12 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-[#20BEC6] focus:border-transparent outline-none transition resize-none"
                        placeholder="Type a thoughtful response..."
                        rows="2"
                        value={newMessage}
                        onChange={e => setNewMessage(e.target.value)}
                        disabled={thread.status === 'Closed' && !isFounder}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSend(e);
                            }
                        }}
                    />
                    <button 
                        type="submit" 
                        disabled={!newMessage.trim() || (thread.status === 'Closed' && !isFounder)}
                        className="absolute right-3 bottom-3 p-2 bg-[#213368] text-white rounded-lg hover:shadow-lg disabled:opacity-50 disabled:shadow-none transition transform hover:-translate-y-0.5"
                    >
                        <PaperAirplaneIcon className="h-4 w-4" />
                    </button>
                </form>
                
                {/* Expectation Management Text */}
                <div className="flex justify-between items-center mt-2 px-1">
                    {thread.status === 'Closed' && !isFounder ? (
                         <p className="text-[10px] text-red-500 flex items-center"><LockClosedIcon className="h-3 w-3 mr-1"/> This thread is closed.</p>
                    ) : (
                         <p className="text-[10px] text-gray-400 italic">
                             Founders usually respond within 24-48 hours. Please be patient.
                         </p>
                    )}
                </div>
            </div>
        </div>
    );
}

// --- SUB-COMPONENT: CREATE MODAL ---
function CreateFeedbackModal({ onClose, onSubmit }) {
    const [subject, setSubject] = useState('');
    const [content, setContent] = useState('');
    const [isAnon, setIsAnon] = useState(false);

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-[#213368]">New Message to Founders</h3>
                    <div 
                        onClick={() => setIsAnon(!isAnon)}
                        className={`cursor-pointer flex items-center px-3 py-1 rounded-full text-xs font-bold transition select-none ${
                            isAnon ? 'bg-purple-100 text-purple-700 ring-1 ring-purple-200' : 'bg-gray-100 text-gray-500'
                        }`}
                    >
                        {isAnon ? <EyeOffIcon className="h-3 w-3 mr-1"/> : <EyeIcon className="h-3 w-3 mr-1"/>}
                        {isAnon ? 'Anonymous ON' : 'Anonymous OFF'}
                    </div>
                </div>
                
                <p className="text-xs text-gray-500 mb-4 bg-blue-50 p-3 rounded border border-blue-100 leading-relaxed">
                    {isAnon 
                        ? "Your identity will be hidden in the conversation view. Founders will see 'Anonymous User'." 
                        : "Your name and department will be visible to the founders to help them contextualize your feedback."}
                </p>

                <div className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Subject</label>
                        <input 
                            className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-[#20BEC6] outline-none text-sm font-medium"
                            placeholder="Briefly summarize your topic..."
                            value={subject}
                            onChange={e => setSubject(e.target.value)}
                            maxLength={80}
                            autoFocus
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Message</label>
                        <textarea 
                            className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-[#20BEC6] outline-none h-32 text-sm"
                            placeholder="Share your ideas, concerns, or feedback in detail..."
                            value={content}
                            onChange={e => setContent(e.target.value)}
                        />
                    </div>
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                    <button onClick={onClose} className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg text-sm font-medium">Cancel</button>
                    <button 
                        onClick={() => onSubmit(subject, content, isAnon)}
                        disabled={!content.trim() || !subject.trim()}
                        className="px-6 py-2 bg-[#213368] text-white font-bold rounded-lg hover:opacity-90 text-sm disabled:opacity-50 transition shadow-sm"
                    >
                        Send Message
                    </button>
                </div>
            </div>
        </div>
    );
}